import { required, useTranslate, useInput } from 'react-admin';
import { Box, Stack, Typography, FormControlLabel, Switch, Button } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { CycleStatus, LeaderboardCycle } from '../../../services/redpacketService';

interface FormProps {
  isCreate: boolean;
  record?: LeaderboardCycle;
}

const LeaderboardCycleForm = (props: FormProps) => {
  const { isCreate, record } = props;
  const t = useTranslate();

  const parseTimeValue = (value: string | number | undefined): dayjs.Dayjs | null => {
    if (!value) return null;
    if (typeof value === 'number') {
      return dayjs(value);
    }
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : null;
  };

  const formatTimeValue = (value: dayjs.Dayjs | null): string => {
    if (!value || !value.isValid()) return '';
    return value.toISOString();
  };

  const {
    field: startTimeField,
    fieldState: { error: startTimeError },
  } = useInput({
    source: 'startTime',
    validate: required(),
    defaultValue: record?.startTime ?? '',
  });

  const {
    field: endTimeField,
    fieldState: { error: endTimeError },
  } = useInput({
    source: 'endTime',
    validate: required(),
    defaultValue: record?.endTime ?? '',
  });

  const { field: rewardAutoTransferField } = useInput({
    source: 'rewardAutoTransferEnabled',
    defaultValue: record?.rewardAutoTransferEnabled ?? false,
  });

  const { field: rewardTransferTimeField } = useInput({
    source: 'rewardTransferTime',
    defaultValue: record?.rewardTransferTime ?? '',
  });

  const { field: clearRewardTransferField } = useInput({
    source: 'clearRewardTransferTime',
    defaultValue: false,
  });

  const transferEnabled = !!rewardAutoTransferField.value;

  return (
    <Stack spacing={3} gap={2}>
      {(record?.status === CycleStatus.UPCOMING || isCreate) && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.redpacket.startTimeRfc3339')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                value={parseTimeValue(startTimeField.value)}
                onChange={(v) => startTimeField.onChange(formatTimeValue(v))}
                format="YYYY-MM-DD HH:mm:ss"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                    error: !!startTimeError,
                    helperText:
                      startTimeError?.message || t('module.redpacket.rfc3339Format'),
                    placeholder: t('module.redpacket.selectStartTime'),
                  },
                }}
              />
            </LocalizationProvider>
          </Box>
        </Stack>
      )}

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={2}
        mb={1}
      >
        <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
          {t('module.redpacket.endTimeRfc3339')}
        </Typography>
        <Box sx={{ flexGrow: 1 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateTimePicker
              value={parseTimeValue(endTimeField.value)}
              onChange={(v) => endTimeField.onChange(formatTimeValue(v))}
              format="YYYY-MM-DD HH:mm:ss"
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: 'small',
                  error: !!endTimeError,
                  helperText: endTimeError?.message || t('module.redpacket.rfc3339Format'),
                  placeholder: t('module.redpacket.selectEndTime'),
                },
              }}
            />
          </LocalizationProvider>
        </Box>
      </Stack>

      <Typography variant="subtitle2" color="text.secondary">
        {t('module.redpacket.rewardAutoTransferSection')}
      </Typography>

      <FormControlLabel
        control={
          <Switch
            checked={transferEnabled}
            onChange={(_, checked) => {
              rewardAutoTransferField.onChange(checked);
            }}
          />
        }
        label={t('module.redpacket.rewardAutoTransferEnabled')}
      />

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={2}
        mb={1}
      >
        <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
          {t('module.redpacket.rewardTransferTime')}
        </Typography>
        <Box sx={{ flexGrow: 1 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateTimePicker
              disabled={!transferEnabled}
              value={parseTimeValue(rewardTransferTimeField.value)}
              onChange={(v) => {
                clearRewardTransferField.onChange(false);
                rewardTransferTimeField.onChange(formatTimeValue(v));
              }}
              format="YYYY-MM-DD HH:mm:ss"
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: 'small',
                  helperText: t('module.redpacket.rewardTransferTimeHint'),
                  placeholder: t('module.redpacket.selectRewardTransferTime'),
                },
              }}
            />
          </LocalizationProvider>
          {!isCreate && (
            <Button
              size="small"
              sx={{ mt: 1 }}
              onClick={() => {
                clearRewardTransferField.onChange(true);
                rewardTransferTimeField.onChange('');
              }}
            >
              {t('module.redpacket.clearRewardTransferTime')}
            </Button>
          )}
        </Box>
      </Stack>
    </Stack>
  );
};

export default LeaderboardCycleForm;
